import { useState, useEffect, useRef } from "react";
import { Timer, Plus, Minus, Play, Pause, RotateCcw } from "lucide-react";

interface Ingredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
}

interface Step {
  id: string;
  title: string;
  content: string;
  timer_seconds?: number;
}

interface RecipeData {
  title: string;
  description?: string;
  base_servings: number;
  ingredients: Ingredient[];
  steps: Step[];
  notes?: string;
}

interface Props {
  data: RecipeData;
}

function formatAmount(amount: number): string {
  if (amount === Math.floor(amount)) return amount.toString();
  const fractions: Record<number, string> = {
    0.25: "¼", 0.5: "½", 0.75: "¾",
    0.33: "⅓", 0.67: "⅔",
  };
  const dec = Math.round((amount % 1) * 100) / 100;
  const whole = Math.floor(amount);
  const frac = fractions[dec] || dec.toFixed(1).replace("0.", ".");
  return whole > 0 ? `${whole}${frac}` : frac;
}

function StepTimer({ seconds }: { seconds: number }) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (running && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(r => {
          if (r <= 1) {
            setRunning(false);
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [remaining, running]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const done = remaining === 0;

  return (
    <div className={`recipe-timer${done ? " done" : ""}`}>
      <Timer size={14} />
      <span className="recipe-timer-time">
        {mins > 0 ? `${mins}m ` : ""}{secs.toString().padStart(2, "0")}s
      </span>
      <button
        className="recipe-timer-btn"
        onClick={() => setRunning(r => !r)}
        disabled={done}
      >
        {running ? <Pause size={12} /> : <Play size={12} />}
      </button>
      <button
        className="recipe-timer-btn"
        onClick={() => { setRemaining(seconds); setRunning(false); }}
      >
        <RotateCcw size={12} />
      </button>
    </div>
  );
}

export default function RecipeDisplay({ data }: Props) {
  const [servings, setServings] = useState(data.base_servings);
  const ratio = servings / data.base_servings;

  const scaleIngredient = (text: string): string => {
    return text.replace(/\{([^}]+)\}/g, (_, id) => {
      const ing = data.ingredients.find(i => i.id === id);
      if (!ing) return `{${id}}`;
      return `${formatAmount(ing.amount * ratio)} ${ing.unit}`.trim();
    });
  };

  return (
    <div className="tool-recipe">
      <div className="recipe-header">
        <h3 className="recipe-title">{data.title}</h3>
        {data.description && <p className="recipe-desc">{data.description}</p>}
        <div className="recipe-servings">
          <button
            className="recipe-servings-btn"
            onClick={() => setServings(s => Math.max(1, s - 1))}
          >
            <Minus size={14} />
          </button>
          <span className="recipe-servings-count">
            {servings} {servings === 1 ? "porsjon" : "porsjoner"}
          </span>
          <button
            className="recipe-servings-btn"
            onClick={() => setServings(s => s + 1)}
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="recipe-ingredients">
        <h4 className="recipe-section-title">Ingredienser</h4>
        <ul className="recipe-ingredient-list">
          {data.ingredients.map(ing => (
            <li key={ing.id} className="recipe-ingredient">
              <span className="recipe-ingredient-amount">
                {formatAmount(ing.amount * ratio)} {ing.unit}
              </span>
              <span className="recipe-ingredient-name">{ing.name}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="recipe-steps">
        <h4 className="recipe-section-title">Fremgangsmåte</h4>
        {data.steps.map((step, i) => (
          <div key={step.id} className="recipe-step">
            <div className="recipe-step-number">{i + 1}</div>
            <div className="recipe-step-content">
              <div className="recipe-step-title">{step.title}</div>
              <div className="recipe-step-text">{scaleIngredient(step.content)}</div>
              {step.timer_seconds && step.timer_seconds > 0 && (
                <StepTimer seconds={step.timer_seconds} />
              )}
            </div>
          </div>
        ))}
      </div>

      {data.notes && (
        <div className="recipe-notes">
          <h4 className="recipe-section-title">Notater</h4>
          <p>{data.notes}</p>
        </div>
      )}
    </div>
  );
}
